import { differenceInMonths, isValid } from 'date-fns';
import { useState, useMemo, forwardRef, useImperativeHandle, useEffect } from 'react';
import { ChevronDown, ChevronUp, LineChart, Table } from 'lucide-react'; // Added icons
import { formatCurrency, formatCompactNumber } from '@/lib/utils';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
  TooltipItem,
  BarController,
  LineController,
  Filler
} from 'chart.js';
import { SafeChart } from '../SafeChart';
import { motion, AnimatePresence } from '@/lib/motion';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  BarController,
  LineController,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface TimelineEntry {
  date: Date;
  year: number;
  annualSavings: number;
  downPaymentBalance: number;
  annualPrincipalPayments: number;
  annualInterestPayments: number;
  mortgageBalance: number;
  totalInterestPaid: number;
  phase: 'saving' | 'mortgage';
  mortgageBalanceStart: number;
  mortgageBalanceEnd: number;
  monthlyMortgagePayment: number;
  actualMonthsPaid: number; // Add this to track actual months paid in the year
}

interface FinancialTimelineProps {
  currentDate: Date;
  purchaseDate: Date;
  monthlySavings: number;
  initialDownPayment: number;
  mortgageAmount: number;
  monthlyMortgagePayment: number;
  interestRate: number;
  amortizationYears: number;
  finalDownPayment: number;
  onCalculate?: (totalInterest: number) => void;
}

export const FinancialTimeline = forwardRef<{ getTotalInterest: () => number }, FinancialTimelineProps>((props, ref) => {
  const [isChartExpanded, setIsChartExpanded] = useState(true);
  const [isTableExpanded, setIsTableExpanded] = useState(false);
  const {
    currentDate,
    purchaseDate,
    monthlySavings,
    initialDownPayment,
    mortgageAmount,
    monthlyMortgagePayment,
    interestRate,
    amortizationYears,
    finalDownPayment,
    onCalculate
  } = props;

  const timeline = useMemo(() => {
    const generateTimeline = (): TimelineEntry[] => {
      const timeline: TimelineEntry[] = [];
      const monthlyRate = interestRate / 12 / 100;
      
      // Ensure valid dates
      const startDate = new Date(currentDate);
      const endDate = new Date(purchaseDate);

      if (!isValid(startDate) || !isValid(endDate)) {
        return timeline;
      }
      
      // Calculate total months for saving phase
      const totalSavingMonths = differenceInMonths(endDate, startDate);
      const remainingSavings = finalDownPayment - initialDownPayment;
      const monthlyContribution = totalSavingMonths > 0 ? remainingSavings / totalSavingMonths : 0;
      
      // Generate timeline for saving phase - one entry per year
      let currentBalance = initialDownPayment;
      let currentYear = startDate.getFullYear();
      const purchaseYear = endDate.getFullYear();
      
      // Handle saving phase
      while (currentYear <= purchaseYear) {
        const yearEnd = currentYear === purchaseYear 
          ? new Date(endDate)
          : new Date(currentYear, 11, 31);
        
        // Calculate savings for this year
        const annualSavings = currentYear === purchaseYear && currentYear === startDate.getFullYear()
          ? monthlyContribution * totalSavingMonths
          : currentYear === purchaseYear
            ? monthlyContribution * differenceInMonths(endDate, new Date(currentYear, 0, 1))
            : currentYear === startDate.getFullYear()
              ? monthlyContribution * (12 - startDate.getMonth())
              : monthlyContribution * 12;
        
        // Add to balance
        currentBalance += annualSavings;
        
        // Add to timeline
        if (currentYear === purchaseYear) {
          // This is the purchase year - show final down payment value
          timeline.push({
            date: new Date(yearEnd),
            year: currentYear,
            annualSavings: annualSavings,
            downPaymentBalance: finalDownPayment,
            annualPrincipalPayments: 0,
            annualInterestPayments: 0,
            mortgageBalance: 0,
            mortgageBalanceStart: 0,
            mortgageBalanceEnd: 0,
            totalInterestPaid: 0,
            phase: 'saving',
            monthlyMortgagePayment: 0,
            actualMonthsPaid: 0
          });
        } else {
          // Regular saving year
          timeline.push({
            date: new Date(yearEnd),
            year: currentYear,
            annualSavings: annualSavings,
            downPaymentBalance: currentBalance,
            annualPrincipalPayments: 0,
            annualInterestPayments: 0,
            mortgageBalance: 0,
            mortgageBalanceStart: 0,
            mortgageBalanceEnd: 0,
            totalInterestPaid: 0,
            phase: 'saving',
            monthlyMortgagePayment: 0,
            actualMonthsPaid: 0
          });
        }
        
        currentYear++;
      }
      
      // Handle mortgage phase - start from purchase year
      let mortgageBalance = mortgageAmount;
      let totalInterestPaid = 0;
      currentYear = purchaseYear;
      
      // Calculate remaining months in purchase year for mortgage payments
      const remainingMonths = 12 - endDate.getMonth();
      let yearlyPrincipal = 0;
      let yearlyInterest = 0;
      let currentMortgageBalance = mortgageAmount;
      
      // Process partial year mortgage payments
      for (let month = 0; month < remainingMonths; month++) {
        const interestPayment = currentMortgageBalance * monthlyRate;
        const principalPayment = Math.min(monthlyMortgagePayment - interestPayment, currentMortgageBalance);
        
        yearlyInterest += interestPayment;
        yearlyPrincipal += principalPayment;
        totalInterestPaid += interestPayment;
        currentMortgageBalance = Math.max(0, currentMortgageBalance - principalPayment);
      }
      
      mortgageBalance = currentMortgageBalance;
      
      // Add mortgage entry for purchase year
      timeline.push({
        date: new Date(currentYear, 11, 31),
        year: currentYear,
        annualSavings: 0,
        downPaymentBalance: 0,
        annualPrincipalPayments: yearlyPrincipal,
        annualInterestPayments: yearlyInterest,
        mortgageBalance: mortgageBalance,
        mortgageBalanceStart: mortgageAmount,
        mortgageBalanceEnd: mortgageBalance,
        totalInterestPaid: totalInterestPaid,
        phase: 'mortgage',
        monthlyMortgagePayment: monthlyMortgagePayment,
        actualMonthsPaid: remainingMonths // Track actual months paid
      });
      
      // Continue with mortgage years until fully paid
      let previousEndBalance = mortgageBalance;
      currentYear++;
      
      while (mortgageBalance > 0) {
        let yearlyInterest = 0;
        let yearlyPrincipal = 0;
        let currentMortgageBalance = previousEndBalance;
        let monthsPaidThisYear = 0;
        
        for (let month = 0; month < 12 && currentMortgageBalance > 0; month++) {
          const interestPayment = currentMortgageBalance * monthlyRate;
          const principalPayment = Math.min(monthlyMortgagePayment - interestPayment, currentMortgageBalance);
          
          yearlyInterest += interestPayment;
          yearlyPrincipal += principalPayment;
          totalInterestPaid += interestPayment;
          currentMortgageBalance = Math.max(0, currentMortgageBalance - principalPayment);
          monthsPaidThisYear++;
        }
        
        mortgageBalance = currentMortgageBalance;
        
        timeline.push({
          date: new Date(currentYear, 11, 31),
          year: currentYear,
          annualSavings: 0,
          downPaymentBalance: 0,
          annualPrincipalPayments: yearlyPrincipal,
          annualInterestPayments: yearlyInterest,
          mortgageBalance: mortgageBalance,
          mortgageBalanceStart: previousEndBalance,
          mortgageBalanceEnd: mortgageBalance,
          totalInterestPaid: totalInterestPaid,
          phase: 'mortgage',
          monthlyMortgagePayment: monthlyMortgagePayment,
          actualMonthsPaid: monthsPaidThisYear // Track actual months paid
        });
        
        previousEndBalance = mortgageBalance;
        currentYear++;
      }
      
      return timeline;
    };

    return generateTimeline();
  }, [
    currentDate,
    purchaseDate,
    monthlySavings,
    initialDownPayment,
    mortgageAmount,
    monthlyMortgagePayment,
    interestRate,
    amortizationYears,
    finalDownPayment
  ]);

  useEffect(() => {
    if (onCalculate && timeline.length > 0) {
      // Find the last mortgage entry to get total interest
      const lastMortgageEntry = [...timeline].reverse().find(entry => entry.phase === 'mortgage');
      if (lastMortgageEntry) {
        onCalculate(lastMortgageEntry.totalInterestPaid);
      }
    }
  }, [timeline, onCalculate]);

  useImperativeHandle(ref, () => ({
    getTotalInterest: () => {
      const lastMortgageEntry = [...timeline].reverse().find(entry => entry.phase === 'mortgage');
      return lastMortgageEntry?.totalInterestPaid || 0;
    }
  }));

  // Updated colors for better differentiation between savings and mortgage
  // Savings phase gets green colors, mortgage phase keeps the blue colors
  const chartData = {
    labels: timeline.map(entry => entry.year.toString()),
    datasets: [
      {
        type: 'bar' as const,
        label: 'Annual Savings',
        data: timeline.map(entry => entry.phase === 'saving' ? entry.annualSavings : 0),
        backgroundColor: 'rgba(0, 81, 59, 0.6)', // Changed to dark green
        yAxisID: 'y1',
        stack: 'savings',
        order: 2,
        barPercentage: 0.8,
        categoryPercentage: 0.9
      },
      {
        type: 'bar' as const,
        label: 'Annual Principal',
        data: timeline.map(entry => entry.annualPrincipalPayments),
        backgroundColor: 'rgba(26, 81, 115, 0.7)', // Primary color (dark blue)
        yAxisID: 'y1',
        stack: 'mortgage',
        order: 2,
        barPercentage: 0.8,
        categoryPercentage: 0.9
      },
      {
        type: 'bar' as const,
        label: 'Annual Interest',
        data: timeline.map(entry => entry.annualInterestPayments),
        backgroundColor: 'rgba(45, 110, 146, 0.5)', // Primary-light color (lighter blue)
        yAxisID: 'y1',
        stack: 'mortgage',
        order: 2,
        barPercentage: 0.8,
        categoryPercentage: 0.9
      },
      {
        type: 'line' as const,
        label: 'Down Payment Balance',
        data: timeline.map(entry => entry.downPaymentBalance),
        borderColor: 'rgb(0, 81, 59)', // Changed to dark green for savings phase
        backgroundColor: 'rgba(0, 81, 59, 0.1)', // Changed to light green for savings phase
        fill: true,
        yAxisID: 'y',
        order: 1,
        tension: 0.4
      },
      {
        type: 'line' as const,
        label: 'Mortgage Balance',
        data: timeline.map(entry => entry.mortgageBalanceEnd),
        borderColor: 'rgb(26, 81, 115)', // Primary color (dark blue)
        backgroundColor: 'rgba(26, 81, 115, 0.1)', // Light blue
        fill: true,
        yAxisID: 'y',
        order: 1,
        tension: 0.4
      }
    ]
  };

  const chartOptions: ChartOptions<'bar' | 'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    animation: {
      duration: 750,
      easing: 'easeInOutQuart'
    },
    transitions: {
      active: {
        animation: {
          duration: 400
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          maxRotation: 0,
          font: {
            size: 10
          },
          autoSkip: true,
          maxTicksLimit: 12
        }
      },
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: 'Balance',
          font: {
            weight: 'bold',
            size: 11
          },
          padding: { bottom: 10 }
        },
        ticks: {
          callback: function(tickValue: number | string) {
            return formatCompactNumber(Number(tickValue));
          },
          font: {
            size: 10
          }
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.1)'
        },
        stacked: false
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        title: {
          display: true,
          text: 'Annual Payments',
          font: {
            weight: 'bold',
            size: 11
          },
          padding: { bottom: 10 }
        },
        ticks: {
          callback: function(tickValue: number | string) {
            return formatCompactNumber(Number(tickValue));
          },
          font: {
            size: 10
          }
        },
        grid: {
          display: false
        },
        stacked: true
      }
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: function(tooltipItem: TooltipItem<'bar' | 'line'>) {
            return `${tooltipItem.dataset.label}: ${formatCurrency(tooltipItem.raw as number)}`;
          }
        }
      },
      legend: {
        position: 'bottom',
        labels: {
          padding: 16,
          boxWidth: 12,
          font: {
            size: 10
          }
        }
      }
    }
  };

  // Filter timeline to remove duplicate years (show only one entry per year)
  const tableTimeline = useMemo(() => {
    const uniqueYears = new Set<number>();
    return timeline.filter(entry => {
      const year = entry.year;
      
      // Special handling for purchase year - allow both saving and mortgage
      if (year === new Date(purchaseDate).getFullYear()) {
        if (!uniqueYears.has(year)) {
          uniqueYears.add(year);
          return true;
        }
        return entry.phase === 'mortgage'; // Only keep the mortgage entry for purchase year
      }
      
      if (!uniqueYears.has(year)) {
        uniqueYears.add(year);
        return true;
      }
      return false;
    });
  }, [timeline, purchaseDate]);

  return (
   <div className="mt-2 space-y-2">
      {/* Chart Section - Only adjusting padding */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
        <button
          onClick={() => setIsChartExpanded(!isChartExpanded)}
          className="w-full px-4 py-4 flex items-center justify-between text-left hover:bg-white">
          <div className="flex items-center">
            <LineChart className="w-5 h-5 text-primary mr-2" />
            <div>
              <h3 className="font-semibold text-gray-900">Financial Timeline Chart</h3>
              <p className="text-sm text-gray-600">
                Visual projection of savings and mortgage
              </p>
            </div>
          </div>
          {isChartExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>

        <AnimatePresence mode="wait" initial={false}>
          {isChartExpanded && (
            <motion.div
              initial={{ height: 'auto' }}
              animate={{ height: 'auto' }}
              exit={{ height: 'auto' }}
              style={{ overflow: 'hidden' }}
              transition={{ duration: 0.3 }}>
                <div className="w-full max-w-full overflow-hidden">
                  <div className="h-[300px] sm:h-[400px]">
                    <SafeChart
                      type="bar"
                      data={chartData}
                      options={chartOptions}
                    />
                  </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Table Section - Only adjusting padding */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
        <button
          onClick={() => setIsTableExpanded(!isTableExpanded)}
          className="w-full px-4 py-4 flex items-center justify-between text-left hover:bg-white">
          <div className="flex items-center">
            <Table className="w-5 h-5 text-primary mr-2" />
            <div>
              <h3 className="font-semibold text-gray-900">Savings and Mortgage Table</h3>
              <p className="text-sm text-gray-600">
                Year-by-year breakdown of savings and mortgage details
              </p>
            </div>
          </div>
          {isTableExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>

        <AnimatePresence mode="wait" initial={false}>
          {isTableExpanded && (
            <motion.div
              initial={{ opacity: 0, height: 'auto' }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 'auto' }}
              style={{ overflow: 'hidden' }}
              transition={{ duration: 0.3 }}>
              <div className="overflow-x-auto">
                <div className="min-w-max">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-900 whitespace-nowrap">Year</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-900 whitespace-nowrap">Phase</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-900 whitespace-nowrap">Savings</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-900 whitespace-nowrap">Down Payment</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-900 whitespace-nowrap">Payment</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-900 whitespace-nowrap">Principal</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-900 whitespace-nowrap">Interest</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-900 whitespace-nowrap">Start Balance</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-900 whitespace-nowrap">End Balance</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-900 whitespace-nowrap">Total Interest</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {tableTimeline.map((entry, index) => (
                        <tr 
                          key={index}
                          className={entry.phase === 'saving' 
                            ? 'bg-[rgba(0,81,59,0.05)] hover:bg-[rgba(0,81,59,0.1)]' // Changed to green for saving phase
                            : 'bg-[rgba(26,81,115,0.05)] hover:bg-[rgba(26,81,115,0.1)]'}>
                          <td className="px-4 py-2 text-xs text-gray-900 whitespace-nowrap">
                            {entry.year}
                          </td>
                          <td className="px-4 py-2 text-xs text-gray-900 whitespace-nowrap">
                            {entry.phase === 'saving' ? 'Saving' : 'Mortgage'}
                          </td>
                          <td className="px-4 py-2 text-xs text-gray-900 whitespace-nowrap">
                            {formatCurrency(entry.annualSavings)}
                          </td>
                          <td className="px-4 py-2 text-xs text-gray-900 whitespace-nowrap">
                            {formatCurrency(entry.downPaymentBalance)}
                          </td>
                          <td className="px-4 py-2 text-xs text-gray-900 whitespace-nowrap">
                            {/* Fixed: Show actual payment based on months paid */}
                            {entry.phase === 'mortgage' && entry.actualMonthsPaid > 0
                              ? formatCurrency(entry.annualPrincipalPayments + entry.annualInterestPayments)
                              : formatCurrency(0)}
                          </td>
                          <td className="px-4 py-2 text-xs text-gray-900 whitespace-nowrap">
                            {formatCurrency(entry.annualPrincipalPayments)}
                          </td>
                          <td className="px-4 py-2 text-xs text-gray-900 whitespace-nowrap">
                            {formatCurrency(entry.annualInterestPayments)}
                          </td>
                          <td className="px-4 py-2 text-xs text-gray-900 whitespace-nowrap">
                            {formatCurrency(entry.mortgageBalanceStart)}
                          </td>
                          <td className="px-4 py-2 text-xs text-gray-900 whitespace-nowrap">
                            {formatCurrency(entry.mortgageBalanceEnd)}
                          </td>
                          <td className="px-4 py-2 text-xs text-gray-900 whitespace-nowrap">
                            {formatCurrency(entry.totalInterestPaid)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
});

FinancialTimeline.displayName = 'FinancialTimeline';